import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Smartphone, Mail, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import mfaService from '../../services/mfaService';
import MFARecoveryLink from './MFARecoveryLink';
import { getMfaResendCooldownSeconds } from '../../utils/mfaCooldown';

const MFAVerification = ({
  onVerificationComplete,
  onCancel,
  userEmail,
  method = 'totp',
  initialResendAvailableAt,
  initialResendCooldownSeconds,
}) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(() =>
    getMfaResendCooldownSeconds(
      {
        next_resend_available_at: initialResendAvailableAt,
        resend_cooldown_seconds: initialResendCooldownSeconds,
      },
      0
    )
  );
  const [rememberDevice, setRememberDevice] = useState(false);

  useEffect(() => {
    setResendCooldown(
      getMfaResendCooldownSeconds(
        {
          next_resend_available_at: initialResendAvailableAt,
          resend_cooldown_seconds: initialResendCooldownSeconds,
        },
        0
      )
    );
  }, [initialResendAvailableAt, initialResendCooldownSeconds]);

  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleVerification = async () => {
    if (!verificationCode.trim()) {
      toast.error('Please enter the verification code');
      return;
    }

    try {
      setLoading(true);
      const result = await mfaService.verifyMFA(verificationCode, method, rememberDevice);
      toast.success('Verification successful!');
      onVerificationComplete(result);
    } catch (error) {
      console.error('Error verifying MFA:', error);
      if (error.response?.status === 400) {
        toast.error('Invalid verification code. Please try again.');
      } else {
        toast.error('Verification failed. Please try again.');
      }
      setVerificationCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (method !== 'email' || resendCooldown > 0 || resending || loading) return;

    try {
      setResending(true);
      const response = await mfaService.resendEmailCode();
      toast.success('New verification code sent to your email');
      setResendCooldown(getMfaResendCooldownSeconds(response));
    } catch (error) {
      console.error('Error resending code:', error);
      if (error.response?.status === 429) {
        const data = error.response.data;
        setResendCooldown(getMfaResendCooldownSeconds(data));
        toast.error(data.cooldown_message || 'Please wait before requesting another code.');
      } else {
        toast.error('Failed to resend code. Please try again.');
      }
    } finally {
      setResending(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setVerificationCode(value);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && verificationCode.length === 6) {
      handleVerification();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10"
      >
        <div className="flex items-start justify-between gap-4 px-6 pb-2 pt-5">
          <div className="flex items-center gap-2.5">
            <Shield className="h-5 w-5 flex-shrink-0 text-gray-400 dark:text-gray-500" strokeWidth={1.75} />
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-50">
              Verify Your Identity
            </h2>
          </div>
          {onCancel && (
            <button
              onClick={onCancel}
              aria-label="Close"
              className="-mr-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            >
              <svg className="h-[18px] w-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <div className="space-y-5 px-6 pb-6 pt-3">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-50">
              {method === 'totp' ? (
                <Smartphone className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              ) : (
                <Mail className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              )}
              {method === 'totp' ? 'Enter Authenticator Code' : 'Check Your Email'}
            </h3>
            
            {method === 'totp' ? (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Open your authenticator app (Google Authenticator, Authy, etc.) and enter the 6-digit code
              </p>
            ) : (
              <div className="mt-1 space-y-1">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  We've sent a 6-digit verification code to:
                </p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {userEmail}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Check your inbox and spam folder. The code expires in 10 minutes.
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="label">
              Verification Code
            </label>
            <input
              type="text"
              value={verificationCode}
              onChange={handleCodeChange}
              onKeyPress={handleKeyPress}
              placeholder="000000"
              className="input h-12 text-center font-mono text-xl tracking-[0.4em]"
              maxLength={6}
              autoFocus
            />
          </div>

          {method === 'email' && (
            <div className="flex justify-center">
              <button
                onClick={handleResendCode}
                disabled={loading || resending || resendCooldown > 0}
                className="flex items-center justify-center text-sm font-medium text-primary-600 transition-colors hover:text-primary-700 disabled:cursor-not-allowed disabled:text-gray-400 dark:text-primary-400 dark:hover:text-primary-300 dark:disabled:text-gray-500"
              >
                <RefreshCw className={`mr-1.5 h-4 w-4 ${resending ? 'animate-spin' : ''}`} />
                {resending
                  ? 'Sending...'
                  : resendCooldown > 0
                  ? `Resend code in ${resendCooldown}s` 
                  : 'Resend code'
                }
              </button>
            </div>
          )}

          <label className="option-row cursor-pointer justify-start gap-3">
            <input
              type="checkbox"
              checked={rememberDevice}
              onChange={(event) => setRememberDevice(event.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300"
            />
            <span>
              <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                Trust this device
              </span>
              <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                Skip the security code on this device for up to 30 days. Do not select this on a shared device.
              </span>
            </span>
          </label>

          <p className="rounded-lg border border-gray-200/70 bg-gray-50 px-3.5 py-3 text-xs leading-relaxed text-gray-600 dark:border-white/[0.07] dark:bg-white/[0.03] dark:text-gray-400">
            <strong className="font-medium text-gray-700 dark:text-gray-300">Security Tip:</strong> {method === 'totp' 
              ? 'Never share your authenticator codes with anyone. FlexCOA staff will never ask for your MFA codes.'
              : 'Never share your email verification codes with anyone. FlexCOA staff will never ask for your codes via phone or chat.'
            }
          </p>

          {/* MFA Recovery Link */}
          <MFARecoveryLink />
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-gray-700 dark:bg-gray-900/40">
          {onCancel && (
            <button
              onClick={onCancel}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleVerification}
            disabled={loading || verificationCode.length !== 6}
            className="btn btn-primary"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"></span>
                Verifying...
              </span>
            ) : (
              'Verify'
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default MFAVerification;
